import { API } from "./api.service";
import { handleResponse } from "../helpers";
import { API_URL } from "../config/appConfig";

const unwrapProfile = (payload) => {
  if (payload == null || typeof payload !== "object") return {};
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return unwrapProfile(payload.data);
  }
  if (payload?.user && typeof payload.user === "object") {
    return unwrapProfile(payload.user);
  }
  return payload;
};

const isMissingRoute = (err) => {
  const code = Number(err?.code || err?.status || 0);
  return code === 404 || code === 405 || code === 501;
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const hasNameFields = (profile) => {
  if (!profile || typeof profile !== "object") return false;
  return Boolean(
    firstDefined(
      profile.firstname,
      profile.firstName,
      profile.lastname,
      profile.lastName
    )
  );
};

/** Prefer non-empty profile fields from primary, then secondary. */
const mergeProfileSources = (primary, secondary) => {
  const a = primary && typeof primary === "object" ? primary : {};
  const b = secondary && typeof secondary === "object" ? secondary : {};
  return {
    ...b,
    ...a,
    firstname: firstDefined(
      a.firstname,
      a.firstName,
      b.firstname,
      b.firstName
    ),
    firstName: firstDefined(
      a.firstname,
      a.firstName,
      b.firstname,
      b.firstName
    ),
    lastname: firstDefined(
      a.lastname,
      a.lastName,
      b.lastname,
      b.lastName
    ),
    lastName: firstDefined(
      a.lastname,
      a.lastName,
      b.lastname,
      b.lastName
    ),
    name: firstDefined(a.name, b.name),
    email: firstDefined(a.email, b.email),
    mobile: firstDefined(
      a.mobile,
      a.mobileNumber,
      b.mobile,
      b.mobileNumber
    ),
    username: firstDefined(a.username, a.userName, b.username, b.userName),
  };
};

const tryThenFallback = async (primary, fallback) => {
  try {
    return await primary();
  } catch (err) {
    if (!fallback || !isMissingRoute(err)) {
      throw err;
    }
    return fallback();
  }
};

const profileBody = (payload = {}) => {
  const body = {
    firstname: payload.firstname || "",
    lastname: payload.lastname || "",
  };
  // Omit unchanged email — IAM rejects re-sending an already-verified email.
  if (payload.email !== undefined) {
    body.email = String(payload.email || "").trim();
  }
  if (payload.mobile) {
    body.mobile = payload.mobile;
  }
  return body;
};

const getSystemProfile = async () => {
  return API.get(`${API_URL.idp}/system/user/profile`, "system_profile")
    .then(handleResponse)
    .then(unwrapProfile);
};

const updateSystemProfile = async (payload) => {
  return API.put(
    `${API_URL.idp}/system/user/profile`,
    "system_profile_update",
    profileBody(payload)
  ).then(handleResponse);
};

const changeSystemOwnPassword = async (payload) => {
  return API.put(
    `${API_URL.idp}/system/user/changepassword`,
    "system_own_change_password",
    { password: payload?.password || payload?.newPassword || "" }
  ).then(handleResponse);
};

const getTenantProfile = async (userId) => {
  // Prefer self profile API (firstname/lastname). Also merge get-by-id when
  // profile is missing or returns without name fields (common IAM gap).
  const bySelf = () =>
    API.get(`${API_URL.idp}/user/profile`, "tenant_profile")
      .then(handleResponse)
      .then(unwrapProfile);
  const byId = userId
    ? () =>
        API.get(`${API_URL.idp}/user/${userId}`, "tenant_profile_by_id")
          .then(handleResponse)
          .then(unwrapProfile)
    : null;

  if (!byId) {
    return bySelf();
  }

  let selfProfile = null;
  let idProfile = null;
  let selfError = null;

  try {
    selfProfile = await bySelf();
  } catch (err) {
    selfError = err;
    selfProfile = null;
  }

  if (!hasNameFields(selfProfile)) {
    try {
      idProfile = await byId();
    } catch {
      idProfile = null;
    }
  }

  if (selfProfile && idProfile) {
    return mergeProfileSources(selfProfile, idProfile);
  }
  if (selfProfile) return selfProfile;
  if (idProfile) return idProfile;

  if (selfError) throw selfError;
  return tryThenFallback(bySelf, byId);
};

const updateTenantProfile = async (payload, userId) => {
  const body = profileBody(payload);
  const bySelf = () =>
    API.put(`${API_URL.idp}/user/profile`, "tenant_profile_update", body).then(
      handleResponse
    );
  const byId = userId
    ? () =>
        API.put(`${API_URL.idp}/user/${userId}`, "tenant_profile_update_by_id", {
          ...body,
          ...(payload?.username ? { username: payload.username } : {}),
        }).then(handleResponse)
    : null;

  return tryThenFallback(bySelf, byId);
};

const changeTenantOwnPassword = async (payload = {}, tenantId) => {
  return API.put(
    `${API_URL.idp}/user/password`,
    "tenant_own_change_password",
    {
      password: payload.password || payload.newPassword || "",
      oldPassword: payload.oldPassword || "",
    },
    {
      headers: {
        ...(tenantId ? { tenantId: String(tenantId).toLowerCase() } : {}),
      },
    }
  ).then(handleResponse);
};

export const accountService = {
  getSystemProfile,
  updateSystemProfile,
  changeSystemOwnPassword,
  getTenantProfile,
  updateTenantProfile,
  changeTenantOwnPassword,
};
